import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './InstructionMenu.scss';

// Instruction images live in Azure Blob Storage, served by an image endpoint that
// takes ?name=<file>. Each step's `img` field is the blob filename directly (e.g.
// "FDRCable.png") — no key→filename map. The endpoint is configurable via the
// `imageEndpoint` prop (default GET /getImage; the update instructions use
// GET /firmware/image, which reads the dedicated `images` container).
const API_BASE = process.env.REACT_APP_API_URL ;

const InstructionMenu = ({ company, instructions, endpoint = 'getInstructions', global = false, imageEndpoint = 'getImage' }) => {

  const imageUrl = (file) =>
    file ? `${API_BASE}/${imageEndpoint}?name=${encodeURIComponent(file)}` : null;

  // Which instruction set to render. `instructions` is the per-key value from the
  // companyMap page; fall back to `company` (companyId) for backward compatibility.
  // Global sets (e.g. the pocketReader update instructions) need no key.
  const instructionKey = instructions || company;

  const [config, setConfig] = useState(null);
  const [error, setError] =  useState(null);
  const [loading, setLoading] =  useState(false);

  useEffect(() =>{
    if (!global && !instructionKey) return;

    const fetchInstruction = async () => {
      setLoading(true);
      try{
        const url = global
          ? `${API_BASE}/${endpoint}`
          : `${API_BASE}/${endpoint}?company=${encodeURIComponent(instructionKey)}`;
        const response = await fetch(url);

        const text = await response.text();

        if(!response.ok) throw new Error (`HTTP ${response.status}: ${text}`);

        const data = JSON.parse(text);
        if (!Array.isArray(data.steps)) throw new Error(`"steps" is not an array`);
        setConfig(data);
      }catch (err){

        console.error("Error fetching instructions:", err);
        setError(err.message);
      } finally{
        setLoading(false);
      }
    };

    fetchInstruction();
  }, [instructionKey, endpoint, global]);

    if (loading) return <div className="instruction-menu">Loading…</div>;

    if (!config) {
    return (
      <div className="instruction-menu">
        <div className="instruction-warning">
          <strong>Error:</strong> {error || `No instructions found${instructionKey ? ` for "${instructionKey}"` : ''}.`}
        </div>
      </div>
    );
  }

  const { warning, steps } =  config;

 return (
    <div className="instruction-menu">
      {warning && (
        <div className="instruction-warning">
          <strong>Notice:</strong> {warning}
        </div>
      )}
      <ol>
        {steps.map((step, idx) =>
          step.header ? (
            <div key={`header-${idx}`} className="instruction-header">
              <strong>{step.header}</strong>
            </div>
          ) : (
            <li key={`step-${idx}`}>
              {step.text}

              {(() => {
                const imgSrc = imageUrl(step.img);
                return imgSrc ? (
                  <div className="step-image">
                    <img src={imgSrc} alt={`Step ${idx + 1}`} />
                  </div>
                ) : null;
              })()}
            </li>
          )
        )}

      </ol>
    </div>
  );
};

InstructionMenu.propTypes = {
  company: PropTypes.string,
  instructions: PropTypes.string,
  endpoint: PropTypes.string,
  global: PropTypes.bool,
  imageEndpoint: PropTypes.string,
};

export default InstructionMenu;
